import logo from "@/assets/favicon_io/android-chrome-192x192.png";
import { config } from "@/shared/lib/config";
import { formatDate, formatInLao } from "@/shared/lib/date-time";
import {
    RECEIPT_LOGO_STYLE_48MM,
    RECEIPT_PRINT_STYLES_48MM,
    buildReceiptProfileSection,
    formatBillAmount,
} from "@/shared/lib/receipt-print-48mm";
import QRCode from "qrcode";

type PackageType = "package" | "private-lesson";

export type PrintSellPackageReceiptParams = {
  packageType: PackageType;
  studentName: string;
  memberId: string;
  packageName: string;
  startDate: string;
  expireDate: string;
  totalAmount: number;
  originalAmount?: number;
  discountAmount?: number;
  discountType?: "percentage" | "fixed";
  discountValue?: number;
  packageId?: string;
  totalHours?: number;
  pricePerHour?: number;
  selectedClassNames?: string[];
  partialPayment?: {
    paidAmount: number;
    remainingAmount: number;
    creditInvoiceId?: string;
  };
};

/**
 * ເປີດກະປຸ່ມພິມໃບຂາຍແພັກເກດ (ບໍ່ມີ dialog)
 */
export async function printSellPackageReceipt(
  params: PrintSellPackageReceiptParams,
): Promise<void> {
  const {
    packageType,
    studentName,
    memberId,
    packageName,
    startDate,
    expireDate,
    totalAmount,
    originalAmount,
    discountAmount = 0,
    discountType,
    discountValue,
    packageId,
    totalHours,
    pricePerHour,
    selectedClassNames,
    partialPayment,
  } = params;

  const finalAmount = totalAmount;
  const originalPrice = originalAmount ?? totalAmount;

  const origin = window.location.origin;
  const profileUrl = `${origin}/public/student/${encodeURIComponent(memberId)}`;
  const qrDataUrl = await QRCode.toDataURL(profileUrl, {
    width: 100,
    margin: 1,
  });
  const profileSection = buildReceiptProfileSection(
    memberId,
    profileUrl,
    qrDataUrl,
  );

  const logoBase64 = await new Promise<string>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } else resolve("");
    };
    img.onerror = () => resolve("");
    img.src = logo;
  });

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const billNo = packageId ? packageId.slice(0, 8).toUpperCase() : "–";
  const dateTime = formatInLao(new Date().toISOString(), "dd/MM/yyyy HH:mm");
  const address = config.brandAddress ?? "VIENTIANE CENTER, 4th floor";
  const addressLo = config.brandAddressLo ?? "ວຽງຈັນເຊັນເຕີ, ຊັ້ນ4";
  const tagline = config.billFooterTagline ?? "Keep Dancing, Keep Moving!";
  const logoImg =
    logoBase64 &&
    `<img src="${logoBase64}" alt="Logo" style="${RECEIPT_LOGO_STYLE_48MM}" />`;

  let itemsLines = `<div class="bill-item-line">${packageName}</div>`;
  if (
    packageType === "private-lesson" &&
    totalHours != null &&
    pricePerHour != null
  ) {
    itemsLines += `<div class="bill-item-line">${totalHours} ຊົ່ວໂມງ × ${pricePerHour.toLocaleString("lo-LA")} ກີບ/ຊົ່ວໂມງ</div>`;
  }
  itemsLines += `<div class="bill-item-line">ໄລຍະຮຽນ: ${formatDate(startDate)} - ${formatDate(expireDate)}</div>`;
  if (
    packageType === "package" &&
    selectedClassNames &&
    selectedClassNames.length > 0
  ) {
    for (const name of selectedClassNames) {
      itemsLines += `<div class="bill-item-line">${name}</div>`;
    }
  }

  const discountHtml =
    discountAmount > 0
      ? `
    <div class="bill-discount">
      <div class="bill-discount-line">ຍອດເງິນ: ${formatBillAmount(originalPrice)} ກີບ</div>
      <div class="bill-discount-line discount-amount">ສ່ວນຫຼຸດ${
        discountType === "percentage" && discountValue != null
          ? ` (${discountValue}%)`
          : discountType === "fixed" && discountValue != null
            ? ` (${formatBillAmount(discountValue)} ກີບ)`
            : ""
      }: -${formatBillAmount(discountAmount)} ກີບ</div>
    </div>`
      : "";

  const partialPrintBlock = partialPayment
    ? `
    <hr class="bill-sep" />
    <div class="bill-line">ຊຳລະແລ້ວ: ${formatBillAmount(partialPayment.paidAmount)} ກີບ</div>
    <div class="bill-line">ຄ້າງຊຳລະ: ${formatBillAmount(partialPayment.remainingAmount)} ກີບ</div>${
      partialPayment.creditInvoiceId
        ? `\n    <div class="bill-line">ເລກໃບເກັບເງິນ: ${partialPayment.creditInvoiceId.slice(0, 8).toUpperCase()}</div>`
        : ""
    }`
    : "";

  const content = `
    <div class="bill-header">
      ${logoImg ?? ""}
      <div class="bill-brand">${config.brandName.toUpperCase()}</div>
      <div class="bill-address">${addressLo}</div>
      <div class="bill-address">${address}</div>
    </div>
    <hr class="bill-sep" />

    <div class="bill-line">ເລກທີໃບ: ${billNo}</div>
    <div class="bill-line">ຊື່: ${studentName}</div>
    <div class="bill-line">ວັນທີ: ${dateTime}</div>
    <hr class="bill-sep" />

    <div class="bill-items">${itemsLines}</div>
    <hr class="bill-sep" />
    ${discountHtml}

    <div class="bill-total-section">
      <div class="bill-total-label">ຍອດລວມທັງໝົດ</div>
      <div class="bill-total-amount">${formatBillAmount(finalAmount)} ກີບ</div>
    </div>
    ${partialPrintBlock}
    <hr class="bill-sep" />
    ${profileSection}
    <hr class="bill-sep" />
    <div class="bill-footer">
      <div class="bill-footer-tagline">${tagline}</div>
      <div class="bill-footer-thanks">ຂອບໃຈທີ່ໃຊ້ບໍລິການ</div>
    </div>
  `;

  printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ບິນ - ${config.brandName}</title>
          <style>${RECEIPT_PRINT_STYLES_48MM}</style>
        </head>
        <body>${content}</body>
      </html>
    `);
  printWindow.document.close();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}
